import type { NotificationFilterParameter } from '#.shared/api/generated/models/notificationFilterParameter.ts'
import { Meta } from '#.shared/api/generated/zod/schemas/meta.zod.ts'
import { NotificationActionType } from '#.shared/api/generated/zod/schemas/notificationActionType.zod.ts'
import { NotificationActorSystem } from '#.shared/api/generated/zod/schemas/notificationActorSystem.zod.ts'
import { NotificationEntityRef as NotificationEntityRefBase } from '#.shared/api/generated/zod/schemas/notificationEntityRef.zod.ts'
import { NotificationType } from '#.shared/api/generated/zod/schemas/notificationType.zod.ts'
import { IanaZone, IsoInstant } from '#.shared/dates/types.ts'
import { instantToDateTime } from '#.shared/dates/utils/convert.ts'
import z from 'zod'

import { EmployeeID } from '../../employees/models/employee'
import { ShiftID } from '../../shifts/models/shift'
import { TimeOffRequestID } from '../../time-off/models/timeOffRequest'

export const NotificationID = z.string().min(1).brand<'NotificationID'>()
export type NotificationID = z.infer<typeof NotificationID>

const localZone = () =>
  IanaZone.parse(Intl.DateTimeFormat().resolvedOptions().timeZone)

/////////////////////////////////////////////////////////////
// Entity references
/////////////////////////////////////////////////////////////

// Entities the client can route to keep their branded ids; anything else
// is kept as display text with its original type for debugging.
const ShiftEntityRef = NotificationEntityRefBase.extend({
  type: z.literal('shift'),
  id: ShiftID,
})

const EmployeeEntityRef = NotificationEntityRefBase.extend({
  type: z.literal('employee'),
  id: EmployeeID,
})

const TimeOffRequestEntityRef = NotificationEntityRefBase.extend({
  type: z.literal('time_off_request'),
  id: z
    .string()
    .min(1)
    .transform((id) => id as TimeOffRequestID),
})

const KnownEntityRef = z.discriminatedUnion('type', [
  ShiftEntityRef,
  EmployeeEntityRef,
  TimeOffRequestEntityRef,
])

const UnknownEntityRef = NotificationEntityRefBase.extend({
  type: z.string(),
}).transform(({ type, ...rest }) => ({
  ...rest,
  type: 'unknown' as const,
  original_type: type,
}))

export const NotificationEntityRef = z.union([KnownEntityRef, UnknownEntityRef])
export type NotificationEntityRef = z.infer<typeof NotificationEntityRef>

/////////////////////////////////////////////////////////////
// Actor
/////////////////////////////////////////////////////////////

export const NotificationActor = z.union([
  NotificationActorSystem,
  NotificationEntityRef,
])
export type NotificationActor = z.infer<typeof NotificationActor>

/////////////////////////////////////////////////////////////
// Actions
/////////////////////////////////////////////////////////////

const NotificationActionBase = z.object({
  type: z.string(),
  label: z.string(),
  target: NotificationEntityRef.nullable(),
})

const KnownNotificationAction = NotificationActionBase.extend({
  type: NotificationActionType,
})

const UnknownNotificationAction = NotificationActionBase.transform(
  ({ type, ...rest }) => ({
    ...rest,
    type: 'unknown' as const,
    original_type: type,
  }),
)

export const NotificationAction = z.union([
  KnownNotificationAction,
  UnknownNotificationAction,
])
export type NotificationAction = z.infer<typeof NotificationAction>

/////////////////////////////////////////////////////////////
// Notification
/////////////////////////////////////////////////////////////

const NotificationBase = z.object({
  id: NotificationID,
  occurred_at: IsoInstant,
  read_at: IsoInstant.nullable(),
  actor: NotificationActor,
  subject: NotificationEntityRef.nullable(),
  related: z.array(NotificationEntityRef),
  summary: z.object({
    title: z.string(),
    text: z.string(),
  }),
  actions: z.array(NotificationAction),
  details: z.record(z.string(), z.unknown()),
})

const KnownNotification = NotificationBase.extend({
  type: NotificationType,
})

const UnknownNotification = NotificationBase.extend({
  type: z.string(),
}).transform(({ type, ...rest }) => ({
  ...rest,
  type: 'unknown' as const,
  original_type: type,
}))

export const Notification = z
  .union([KnownNotification, UnknownNotification])
  .transform(({ occurred_at, read_at, ...rest }) => ({
    ...rest,
    occurred_at: instantToDateTime(occurred_at, localZone()),
    read_at: read_at ? instantToDateTime(read_at, localZone()) : null,
  }))
export type Notification = z.infer<typeof Notification>

export function parseNotification(input: unknown): Notification | null {
  const parsed = Notification.safeParse(input)
  if (parsed.success) return parsed.data

  console.error('Notification contract violation', parsed.error, input)

  return null
}

export const NotificationPage = z.object({
  data: z.array(z.unknown()),
  meta: Meta,
})

/////////////////////////////////////////////////////////////
// Filters
/////////////////////////////////////////////////////////////

export type NotificationReadFilter =
  (typeof NotificationFilterParameter)[keyof typeof NotificationFilterParameter]

// The API accepts exact types or prefixes ending in `.*`; the UI offers these groups.
export const NOTIFICATION_TYPE_GROUPS = {
  all: [],
  shift_offers: ['shift.offer.*'],
  shift_fill: ['shift.filled', 'shift.unfilled'],
  shift_assignments: ['shift.assignment.*'],
  time_off: ['time_off.*'],
  broadcasts: ['broadcast.*'],
} as const satisfies Record<string, readonly string[]>
export type NotificationTypeGroup = keyof typeof NOTIFICATION_TYPE_GROUPS
